import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { tournamentService } from "../../services/api/tournamentService";
import { apiClient } from "../../services/api/apiService";
import teamService from "../../services/api/teamService";
import GameLayout from "../layout/GameLayout";
import FloatingCard from "../ui/FloatingCard";
import FloatingButton from "../ui/FloatingButton";
import StatusBadge from "../ui/StatusBadge";
import TournamentBracket from "./TournamentBracket";
import UserSearchInput from "../match/UserSearchInput";
import { eid } from "../../utils/entityId";
import paymentService from "../../services/api/paymentService";
import { usePaymentsSetup } from "../payments/PriceField";
import { RefundPolicyNote, leaveMessage, HostPaymentsLink } from "../payments/RefundPolicy";
import PaidSummary from "../payments/PaidSummary";
import { formatMoney } from "../../utils/money";
import { isSystemAdmin } from "../../utils/rbac";
import { formatDateOnly } from "../../utils/dateOnly";
import { getMatchFormatTerms } from "../../utils/matchFormatTerms";
import "../../styles/features/tournaments.css";

const TABS = [
  { key: "overview",  label: "📋 Overview" },
  { key: "teams",     label: "🏅 Teams" },
  { key: "schedule",  label: "⚔️ Schedule" },
  { key: "matches",   label: "🎮 Matches" },
  { key: "standings", label: "🏆 Standings" },
];

// Info shown in the ScheduleTab for each tournament format
const SCHEDULE_FORMAT_INFO = {
  single_elimination: {
    icon: "🏆",
    label: "Single Elimination",
    description:
      "One loss and you're out. Every match is all-or-nothing. " +
      "Participants are seeded so the top seeds can only meet in the final. " +
      "Best for fast events with many teams — delivers maximum drama in the fewest matches.",
  },
  double_elimination: {
    icon: "🔄",
    label: "Double Elimination",
    description:
      "Teams get a second life. A loss in the Winners Bracket drops you into the Losers Bracket — " +
      "a second loss ends the run. The Losers Bracket champion faces the Winners Bracket champion in the grand final; " +
      "if the Losers Bracket champion wins it, a reset match decides the title. " +
      "Best for competitive leagues where one bad game shouldn't eliminate you.",
  },
  round_robin: {
    icon: "🔁",
    label: "Round Robin",
    description:
      "Every participant plays every other participant exactly once. " +
      "Matches are grouped into rounds (circle/Berger method) so each round has no repeat opponents. " +
      "The participant with the most points wins. Best for 4–12 teams where consistency matters.",
  },
  swiss_system: {
    icon: "♟️",
    label: "Swiss System",
    description:
      "Play a fixed number of rounds — no one is ever eliminated. " +
      "Round 1 pairs the draw; every later round pairs participants on the same score, never the same opponent twice. " +
      "With an odd number, one participant per round gets a bye worth a win. " +
      "Best for large tournaments where a full round-robin isn't practical (e.g. chess, card games).",
  },
  group_knockout: {
    icon: "🧩",
    label: "Groups + Knockout",
    description:
      "Participants are seeded into groups that each play a round robin (3 points a win, 1 a draw). " +
      "The top finishers of every group go into a seeded knockout, drawn so group-mates don't meet in its first round. " +
      "The knockout is created once every group match is complete.",
  },
};

/** Formats whose later matches the backend creates from results (POST /tournaments/:id/advance). */
const PROGRESSIVE_FORMATS = ["double_elimination", "swiss_system", "group_knockout"];

const TournamentDetails = ({ user }) => {
  const { id } = useParams();
  const navigate = useNavigate();

  // ── Core state ─────────────────────────────────────────────────────────────
  const [tournament, setTournament] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [generatingSchedule, setGeneratingSchedule] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [userRegistration, setUserRegistration] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [removeConfirmReg, setRemoveConfirmReg] = useState(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  // bye / auto-advance info from last schedule generation
  const [byeAdvancers, setByeAdvancers] = useState([]); // [{id, name, type}]
  // Custom schedule builder
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [customPairs, setCustomPairs] = useState([{ p1: "", p2: "" }]);
  const [savingCustom, setSavingCustom] = useState(false);
  // Next-round generation
  const [generatingNextRound, setGeneratingNextRound] = useState(false);
  // Stage / standings / champion for progressive formats (GET /tournaments/:id/progress)
  const [progress, setProgress] = useState(null);
  // Doubles entry: the partner the player enters with (TN-B11)
  const [showPartnerModal, setShowPartnerModal] = useState(false);
  const [partner, setPartner] = useState(null);

  // Tab state — persisted in URL (?tab=teams etc.)
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = TABS.some((t) => t.key === searchParams.get("tab")) ? searchParams.get("tab") : "overview";
  const setActiveTab = (tab) => setSearchParams((prev) => { prev.set("tab", tab); return prev; }, { replace: true });

  // Entry fees: paid in the app while payments are on
  const paymentsSetup = usePaymentsSetup();
  const [paying, setPaying] = useState(false);
  const paymentReturn = searchParams.get("payment");
  const paymentId = searchParams.get("id");

  // Team registration modal
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [userTeams, setUserTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [loadingTeams, setLoadingTeams] = useState(false);

  // Invite team
  const [inviteSearch, setInviteSearch] = useState("");
  const [inviteResults, setInviteResults] = useState([]);
  const [inviteSearching, setInviteSearching] = useState(false);
  const [invitingTeamId, setInvitingTeamId] = useState(null);

  // ── formatters ────────────────────────────────────────────────────────────

  // Tournament start/end and registration dates are calendar dates (entered
  // via date inputs, stored at midnight) — format without timezone shifting.
  const formatDate = (d) =>
    formatDateOnly(d, { weekday: "long", year: "numeric", month: "long", day: "numeric" }, "—");

  const formatDateTime = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleString("en-US", { weekday: "short", year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const formatStatus = (s) => {
    if (!s) return "Unknown";
    return s.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
  };

  // ── Data loading ──────────────────────────────────────────────────────────

  const refreshTournament = useCallback(async () => {
    const res = await tournamentService.getTournamentById(id);
    setTournament(res.data);
  }, [id]);

  const refreshRegistrations = useCallback(async () => {
    try {
      const regRes = await tournamentService.getTournamentRegistrations(id);
      const regs = regRes.data || [];
      setRegistrations(regs);
      if (user) {
        setUserRegistration(regs.find((r) => r.user_id === user.id) || null);
      }
    } catch { /* ignore */ }
  }, [id, user]);

  const loadMatches = useCallback(async () => {
    try {
      const res = await apiClient.get(`/matches?tournament_id=${id}&limit=100`);
      const raw = res.data?.data;
      const list = Array.isArray(raw) ? raw
        : Array.isArray(raw?.matches) ? raw.matches
        : [];
      setMatches(list);
    } catch { setMatches([]); }
    try {
      const res = await apiClient.get(`/tournaments/${id}/progress`);
      const data = res.data?.data;
      // Round robin is not progressive but has server standings too (TN-B15)
      setProgress(data?.progressive || data?.standings ? data : null);
    } catch { setProgress(null); }
  }, [id]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await tournamentService.getTournamentById(id);
        const t = res.data;
        setTournament(t);

        // Always load full registrations list (endpoint is auth-only, no permission required)
        try {
          const regRes = await tournamentService.getTournamentRegistrations(id);
          const regs = regRes.data || [];
          setRegistrations(regs);
          // Find current user's own registration in the list
          if (user) {
            setUserRegistration(regs.find((r) => r.user_id === user.id) || null);
          }
        } catch {
          // Fallback for unauthenticated users: try personal registrations
          if (user) {
            try {
              const myRegs = await tournamentService.getUserTournamentRegistrations();
              const myData = Array.isArray(myRegs.data) ? myRegs.data
                : Array.isArray(myRegs.data?.registrations) ? myRegs.data.registrations
                : [];
              const mine = myData.find((r) => String(r.tournament_id) === String(id));
              if (mine) setUserRegistration(mine);
            } catch { /* ignore */ }
          }
        }

        await loadMatches();
      } catch (err) {
        setError(err.message || "Failed to load tournament");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, user, loadMatches]);

  // Refresh the data a tab shows whenever the user switches to it, so tabs do
  // not keep stale registrations / matches / standings (BUG-63). Matches and
  // registrations can change elsewhere (invites accepted, games scored live).
  const isFirstTabRender = React.useRef(true);
  useEffect(() => {
    if (isFirstTabRender.current) { isFirstTabRender.current = false; return; }
    if (["overview", "teams", "schedule"].includes(activeTab)) {
      refreshRegistrations();
      refreshTournament().catch(() => { /* keep current data */ });
    }
    if (["schedule", "matches", "standings"].includes(activeTab)) {
      loadMatches();
    }
  }, [activeTab, refreshRegistrations, refreshTournament, loadMatches]);

  // Back from Stripe Checkout. Cancelled: close it now. Paid: the webhook
  // can take a few seconds, so look again shortly.
  useEffect(() => {
    if (!paymentReturn) return undefined;
    let timer;
    setSearchParams((prev) => { prev.delete("payment"); prev.delete("id"); return prev; }, { replace: true });
    if (paymentReturn === "cancelled" && paymentId) {
      paymentService.cancelPayment(paymentId).catch(() => {});
      setSuccess("Payment cancelled: your entry is saved but the fee is unpaid.");
    } else if (paymentReturn === "success") {
      setSuccess("💳 Entry fee paid. Thank you!");
      timer = setTimeout(() => refreshRegistrations(), 3000);
    }
    return () => clearTimeout(timer);
  }, [paymentReturn, paymentId, setSearchParams, refreshRegistrations]);

  // ── Actions ───────────────────────────────────────────────────────────────

  const isDoublesTournament = ["doubles", "mixed"].includes(tournament?.play_format);

  const handleRegister = async () => {
    if (!user) { navigate("/login"); return; }
    if (isDoublesTournament) {
      setError(""); setSuccess("");
      setPartner(null);
      setShowPartnerModal(true);
      return;
    }
    const sportType = tournament?.sport_type;
    if (sportType === "team") {
      setError(""); setSuccess("");
      setLoadingTeams(true);
      setShowTeamModal(true);
      try {
        const res = await teamService.getUserTeams({
          status: "active",
          ...(tournament?.sport_name ? { sport_name: tournament.sport_name } : {}),
        });
        const raw = res?.data;
        const teams = Array.isArray(raw) ? raw
          : Array.isArray(raw?.teams) ? raw.teams
          : Array.isArray(raw?.data) ? raw.data
          : [];
        setUserTeams(teams);
        setSelectedTeamId(teams.length > 0 ? String(teams[0].id) : "");
      } catch { setUserTeams([]); }
      finally { setLoadingTeams(false); }
    } else {
      await submitRegistration({});
    }
  };

  const submitRegistration = async (extraData) => {
    if (!user) { navigate("/login"); return; }
    setRegistering(true);
    setError(""); setSuccess("");
    try {
      const res = await tournamentService.registerForTournament(id, extraData);
      setUserRegistration(res.data);
      // A paid entry goes straight on to Stripe; paying can also wait
      if (feeDue(res.data)) {
        await startEntryPayment();
        return;
      }
      setSuccess("🎉 Successfully registered for tournament!");
      await refreshTournament();
      await refreshRegistrations();
      setShowTeamModal(false);
      setShowPartnerModal(false);
      setActiveTab("teams"); // jump to teams tab to show registration
    } catch (err) {
      setError(err.message);
      // Close modal when the team is already registered — the error banner below is enough
      if (err.message?.toLowerCase().includes("already registered") ||
          err.message?.toLowerCase().includes("already registered")) {
        setShowTeamModal(false);
      }
    } finally {
      setRegistering(false);
    }
  };

  const entryFeeCents = Math.round((Number(tournament?.entry_fee) || 0) * 100);
  const entryFee = entryFeeCents > 0 ? formatMoney(entryFeeCents) : null;
  const feeDue = (reg) => Boolean(entryFee) && paymentsSetup.enabled && reg?.payment_status === "pending"
    && reg?.registration_status !== "rejected" && !["completed", "cancelled"].includes(tournament?.status);

  const startEntryPayment = async () => {
    setPaying(true);
    setError("");
    try {
      const res = await tournamentService.payEntryFee(id);
      if (res?.data?.checkoutUrl) {
        window.location.assign(res.data.checkoutUrl);
        return;
      }
      if (res?.data?.status === "paid") {
        // An earlier payment (withdrew, then entered again) already covers it
        setSuccess(`✅ ${res.data.message || "Your entry fee is paid"}`);
        await refreshRegistrations();
      }
    } catch (err) {
      setError(err.message);
      await refreshRegistrations();
    }
    setPaying(false);
  };

  const handleOpenRegistration = async () => {
    setError(""); setSuccess("");
    try {
      await tournamentService.openTournamentRegistration(id);
      setSuccess("✅ Registration opened!");
      await refreshTournament();
    } catch (err) { setError(err.message); }
  };

  const handleStatusChange = async (newStatus) => {
    setError(""); setSuccess("");
    try {
      const labels = {
        cancelled:            "Tournament cancelled",
        in_progress:          "🚀 Tournament started!",
        registration_closed:  "🔒 Registration closed",
        completed:            "🏁 Tournament completed",
      };
      if (newStatus === "registration_closed") {
        await tournamentService.closeRegistration(id);
      } else if (newStatus === "in_progress") {
        await tournamentService.startTournament(id);
      } else if (newStatus === "completed") {
        await tournamentService.completeTournament(id);
      } else if (newStatus === "cancelled") {
        await tournamentService.cancelTournament(id);
      } else {
        await tournamentService.updateTournament(id, { status: newStatus });
      }
      await refreshTournament();
      setSuccess(`✅ ${labels[newStatus] || "Tournament updated"}`);
    } catch (err) { setError(err.message); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setError(""); setSuccess("");
    try {
      await tournamentService.deleteTournament(id);
      setTimeout(() => navigate("/tournaments"), 1500);
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleRegistrationStatus = async (regId, status) => {
    setError(""); setSuccess("");
    try {
      await apiClient.patch(`/tournaments/${id}/registrations/${regId}`, { status });
      setSuccess(`✅ Registration ${status}`);
      await refreshRegistrations();
      await refreshTournament();
    } catch (err) { setError(err.response?.data?.message || err.message); }
  };

  const handleRemoveRegistration = async (regId) => {
    setError(""); setSuccess("");
    try {
      await apiClient.delete(`/tournaments/${id}/registrations/${regId}`);
      setSuccess("✅ Registration removed");
      if (userRegistration?.id === regId) setUserRegistration(null);
      await refreshRegistrations();
      await refreshTournament();
    } catch (err) { setError(err.response?.data?.message || err.message); }
  };

  const handleInviteTeamSearch = async (query) => {
    setInviteSearch(query);
    if (!query || query.trim().length < 2) { setInviteResults([]); return; }
    setInviteSearching(true);
    try {
      const tournamentSport = tournament?.sport_name || tournament?.sport || "";
      // Filter by sport so we default to matching sport teams
      const sportParam = tournamentSport ? `&sport=${encodeURIComponent(tournamentSport)}` : "";
      const res = await apiClient.get(`/teams?search=${encodeURIComponent(query.trim())}&limit=8${sportParam}`);
      const raw = res.data?.data;
      let list = Array.isArray(raw) ? raw : Array.isArray(raw?.teams) ? raw.teams : [];
      // If sport-specific search returned nothing, fallback to all sports with mismatch flag
      if (list.length === 0 && tournamentSport) {
        const fallback = await apiClient.get(`/teams?search=${encodeURIComponent(query.trim())}&limit=8`);
        const fb = fallback.data?.data;
        const fbList = Array.isArray(fb) ? fb : Array.isArray(fb?.teams) ? fb.teams : [];
        list = fbList.map((t) => ({ ...t, _sportMismatch: true }));
      }
      setInviteResults(list);
    } catch { setInviteResults([]); }
    finally { setInviteSearching(false); }
  };

  const handleInviteTeam = async (teamId, teamName, sportMismatch = false) => {
    setError(""); setSuccess("");
    // First-level validation: already registered (any status)
    const alreadyRegistered = registrations.some(
      (r) => (r.team_id ?? r.user_id) === teamId
    );
    if (alreadyRegistered) {
      setError(`${teamName} is already registered or has a pending invitation for this tournament.`);
      return;
    }
    setInvitingTeamId(teamId);
    try {
      await tournamentService.inviteTeamToTournament(id, teamId);
      const msg = sportMismatch
        ? `✅ Invitation sent to ${teamName}. Note: this team plays a different sport — they may not meet tournament requirements.`
        : `✅ Invitation sent to ${teamName}`;
      setSuccess(msg);
      setInviteSearch(""); setInviteResults([]);
    } catch (err) {
      const msg = err.message || '';
      if (/pending invitation/i.test(msg)) {
        setError(`${teamName} already has a pending invitation.`);
      } else {
        setError(msg);
      }
    } finally { setInvitingTeamId(null); }
  };

  const handleGenerateSchedule = async () => {
    const seedingAllowed = ["registration_closed", "in_progress"].includes(tournament?.status);
    if (!seedingAllowed) {
      setError("Schedule can only be generated after registration is closed.");
      return;
    }
    const approved = registrations.filter((r) => r.registration_status === "approved");
    if (approved.length < 2) {
      setError("Need at least 2 approved participants to generate a schedule.");
      return;
    }
    // If matches already exist, show confirm modal instead of generating immediately
    if (matches.length > 0) {
      setShowClearConfirm(true);
      return;
    }
    await _doGenerate();
  };

  const _doGenerate = async () => {
    setShowClearConfirm(false);
    setGeneratingSchedule(true);
    setError(""); setSuccess("");
    try {
      const res = await tournamentService.generateSchedule(id, { clearExisting: true, shuffle: false });
      const { totalCreated, metadata } = res.data || res;
      const formatName = (metadata?.format || tournament.tournament_type).replace(/_/g, " ");

      // Resolve bye-advancer names from registrations
      const byeIds = Array.isArray(metadata?.byeAdvancers) ? metadata.byeAdvancers : [];
      const approvedRegs = registrations.filter((r) => r.registration_status === "approved");
      const resolved = byeIds.map((pid) => {
        const reg = approvedRegs.find((r) =>
          (tournament?.sport_type === "team" ? r.team_id : r.user_id) === pid
        );
        const name = reg?.team_name
          || (reg ? `${reg.first_name || ""} ${reg.last_name || ""}`.trim() : null)
          || reg?.username
          || `Participant #${pid}`;
        return { id: pid, name, type: reg?.team_id ? "team" : "user" };
      });
      setByeAdvancers(resolved);

      setSuccess(
        `⚔️ ${totalCreated} ${totalCreated === 1 ? "match" : "matches"} created! Format: ${formatName}. ` +
        (resolved.length > 0 ? `${resolved.map((b) => b.name).join(", ")} advance(s) with a bye.` : "")
      );
      await loadMatches();
      setSearchParams((prev) => { prev.set("tab", "matches"); return prev; }, { replace: true });
    } catch (err) {
      setError(err.message || "Failed to generate schedule");
    } finally {
      setGeneratingSchedule(false);
    }
  };

  // Clear byeAdvancers when matches are cleared externally
  const handleClearMatches = async () => {
    setByeAdvancers([]);
    await _doGenerate();
  };

  // ── Custom schedule ───────────────────────────────────────────────────────
  const handleSaveCustomSchedule = async () => {
    const validPairs = customPairs.filter((p) => p.p1 && p.p2 && p.p1 !== p.p2);
    if (validPairs.length === 0) {
      setError("Add at least one valid match pair (both teams must be different).");
      return;
    }
    setSavingCustom(true);
    setError(""); setSuccess("");
    try {
      const approvedRegs = registrations.filter((r) => r.registration_status === "approved");
      const getParticipant = (val) => {
        const reg = approvedRegs.find((r) => String(r.team_id || r.user_id) === String(val));
        return reg ? { id: reg.team_id || reg.user_id, type: reg.team_id ? "team" : "user" } : null;
      };
      const sportName = (tournament?.sport_name || "").toLowerCase();
      const gameRules = (() => {
        if (sportName.includes("volleyball")) return { total_games: 5, games_to_win: 3, points_to_win: 25, win_by_margin: 2, max_points: 30 };
        if (sportName.includes("basketball") || sportName.includes("soccer") || sportName.includes("football")) return { total_games: 1, games_to_win: 1, points_to_win: null, win_by_margin: 1, max_points: null };
        return { total_games: 3, games_to_win: 2, points_to_win: 21, win_by_margin: 2, max_points: null };
      })();
      let created = 0;
      for (let i = 0; i < validPairs.length; i++) {
        const pair = validPairs[i];
        const p1 = getParticipant(pair.p1);
        const p2 = getParticipant(pair.p2);
        if (!p1 || !p2) continue;
        await apiClient.post("/matches", {
          tournament_id: tournament?.id ?? parseInt(id),
          sport_id: tournament.sport_id,
          participant_1_id: p1.id,
          participant_1_type: p1.type,
          participant_2_id: p2.id,
          participant_2_type: p2.type,
          match_type: "tournament",
          round: 1,
          match_number: i + 1,
          status: "scheduled",
          match_config: { game_rules: gameRules },
        });
        created++;
      }
      setSuccess(`✅ ${created} custom match(es) created!`);
      setShowCustomModal(false);
      setCustomPairs([{ p1: "", p2: "" }]);
      await loadMatches();
      setSearchParams((prev) => { prev.set("tab", "matches"); return prev; }, { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to create matches");
    } finally {
      setSavingCustom(false);
    }
  };

  // ── Generate Next Round ───────────────────────────────────────────────────
  const handleGenerateNextRound = async (completedRound) => {
    try {
      setGeneratingNextRound(true);
      setError("");
      const res = await apiClient.post(`/tournaments/${id}/generate-next-round`, {
        round: completedRound,
      });
      const { nextRound, totalCreated } = res.data?.data || {};
      setSuccess(`🏆 Round ${nextRound} generated — ${totalCreated} ${totalCreated === 1 ? "match" : "matches"} created!`);
      await loadMatches();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to generate next round");
    } finally {
      setGeneratingNextRound(false);
    }
  };

  // ── Advance a progressive bracket (double elim / Swiss / groups + knockout) ──
  const handleAdvanceBracket = async () => {
    try {
      setGeneratingNextRound(true);
      setError("");
      const res = await apiClient.post(`/tournaments/${id}/advance`, {});
      const { totalCreated } = res.data?.data || {};
      setSuccess(`🏆 ${totalCreated} ${totalCreated === 1 ? "match" : "matches"} created!`);
      await loadMatches();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to advance the bracket");
    } finally {
      setGeneratingNextRound(false);
    }
  };

  // ── Derived ───────────────────────────────────────────────────────────────

  // Lifecycle actions are creator-only on the backend (tournamentService).
  const isOrganizer = user && tournament && Number(tournament.created_by) === Number(user.id);
  // generate-next-round also lets super/platform admins advance a bracket (BUG-32).
  const canAdvanceBracket = isOrganizer || isSystemAdmin(user);
  const canRegister = tournament?.status === "registration_open" && !userRegistration &&
    (tournament.current_participants ?? 0) < (tournament.max_participants ?? 0);

  // ── Standings computation ─────────────────────────────────────────────────
  const standings = (() => {
    const map = {};
    const addParticipant = (pid, name) => {
      if (!pid) return;
      if (!map[pid]) map[pid] = { id: pid, name: name || `#${pid}`, played: 0, won: 0, lost: 0, drawn: 0, points: 0 };
    };
    matches.forEach((m) => {
      const p1 = m.participant1Id ?? m.participant_1_id ?? m.home_team_id;
      const p2 = m.participant2Id ?? m.participant_2_id ?? m.away_team_id;
      const p1Name = m.participant1Name || m.participant_1_name || m.home_team_name || m.participant_1_username;
      const p2Name = m.participant2Name || m.participant_2_name || m.away_team_name || m.participant_2_username;
      addParticipant(p1, p1Name);
      addParticipant(p2, p2Name);
      if (m.status !== "completed") return;
      map[p1].played++; map[p2].played++;

      const isDraw = m.resultType === "draw" || m.result_type === "draw" || m.resultType === "tie" || m.result_type === "tie";
      const winnerId = m.winnerId ?? m.winner_id;

      // Try score-based fallback if winner_id not set and not a draw
      const s1 = m.home_score ?? m.score_team1 ?? null;
      const s2 = m.away_score ?? m.score_team2 ?? null;

      if (isDraw) {
        map[p1].drawn++; map[p1].points++;
        map[p2].drawn++; map[p2].points++;
      } else if (winnerId) {
        if (winnerId === p1) {
          map[p1].won++; map[p1].points += 3; map[p2].lost++;
        } else {
          map[p2].won++; map[p2].points += 3; map[p1].lost++;
        }
      } else if (s1 != null && s2 != null) {
        // Score-based fallback
        if (s1 > s2)      { map[p1].won++; map[p1].points += 3; map[p2].lost++; }
        else if (s2 > s1) { map[p2].won++; map[p2].points += 3; map[p1].lost++; }
        else              { map[p1].drawn++; map[p1].points++; map[p2].drawn++; map[p2].points++; }
      }
    });
    return Object.values(map).sort((a, b) => b.points - a.points || b.won - a.won);
  })();

  // ── Loading / not-found guards ────────────────────────────────────────────

  if (loading) {
    return (
      <GameLayout onLogout={() => {}} profile={user} showFloatingNav>
        <div className="tournaments-gaming-layout">
          <div className="loading-gaming-floating">
            <FloatingCard variant="primary" size="large" className="delay-1">
              <div className="loading-content-gaming">
                <div className="loading-spinner-gaming"></div>
                <h3>Loading Tournament...</h3>
              </div>
            </FloatingCard>
          </div>
        </div>
      </GameLayout>
    );
  }

  if (!tournament) {
    return (
      <GameLayout onLogout={() => {}} profile={user} showFloatingNav>
        <div className="tournaments-gaming-layout">
          <FloatingCard variant="outline" size="large" className="delay-1">
            <div className="empty-content-gaming">
              <div className="empty-icon">🏟️</div>
              <h3>Tournament Not Found</h3>
              <FloatingButton variant="primary" size="medium" onClick={() => navigate("/tournaments")} icon="←">
                <span className="btn-text">Back to Tournaments</span>
              </FloatingButton>
            </div>
          </FloatingCard>
        </div>
      </GameLayout>
    );
  }

  // ── Tab content ───────────────────────────────────────────────────────────

  const thStyle = { padding: "0.75rem", textAlign: "left", color: "#a78bfa", fontWeight: 600, fontSize: "0.85rem" };
  const tdStyle = (extra = {}) => ({ padding: "0.75rem", color: "#e2e8f0", ...extra });
  const rowStyle = { borderBottom: "1px solid rgba(255,255,255,0.06)" };

  const OverviewTab = () => (
    <FloatingCard variant="secondary" size="large" className="delay-2">
      <h3 className="form-section-title" style={{ marginBottom: "1.2rem" }}>📋 Tournament Information</h3>
      <div className="tournament-details-gaming">
        {[
          ["🏆", "Sport",        tournament.sport_name || "—"],
          ["⚔️", "Format",       formatStatus(tournament.tournament_type)],
          ...((() => {
            const mf = tournament.bracket_settings?.match_format;
            if (!mf) return [];
            const label = mf === "best_of_1" ? `1 ${getMatchFormatTerms(tournament.sport_name).unit === "game" ? "Game" : "Set"}` : mf === "best_of_3" ? "Best of 3" : mf === "best_of_5" ? "Best of 5" : mf.replace(/_/g, " ");
            return [["⚙️", "Match Format", label]];
          })()),
          ["🎯", "Sport Type",   formatStatus(tournament.sport_type || "individual")],
          ["👥", "Participants", `${tournament.current_participants || 0} / ${tournament.max_participants}`],
          ["📅", "Start Date",   formatDate(tournament.start_date)],
          ["🏁", "End Date",     formatDate(tournament.end_date)],
          ...(tournament.registration_start ? [["🔓", "Reg. Opens",  formatDate(tournament.registration_start)]] : []),
          ...(tournament.registration_end   ? [["🔒", "Reg. Closes", formatDate(tournament.registration_end)]]   : []),
          ...(tournament.entry_fee > 0      ? [["💰", "Entry Fee",   `$${tournament.entry_fee}`]]                    : []),
          ...(tournament.prize_pool > 0     ? [["💎", "Prize Pool",  `$${tournament.prize_pool}`]]                   : []),
          ["🧑‍💼", "Organizer",   tournament.created_by_username || "—", tournament.created_by ? () => navigate(`/players/${tournament.created_by_short_code || tournament.created_by_uuid || tournament.created_by}`) : undefined],
          ...(tournament.organization_name  ? [["🏢", "Organization", tournament.organization_name]] : []),
          ...(tournament.skill_level ? [["🏅", "Skill Level", tournament.skill_level.charAt(0).toUpperCase() + tournament.skill_level.slice(1)]] : []),
        ].map(([icon, label, value, onClick]) => (
          <div key={label} className="detail-item">
            <span className="detail-icon">{icon}</span>
            <span className="detail-label">{label}</span>
            <span
              className="detail-value"
              style={onClick ? { cursor: 'pointer', color: '#b8acff' } : undefined}
              onClick={onClick}
            >{value}</span>
          </div>
        ))}
        {tournament.description && (
          <div className="detail-item full-width">
            <span className="detail-icon">📝</span>
            <span className="detail-label">Description</span>
            <p className="detail-description">{tournament.description}</p>
          </div>
        )}
      </div>
    </FloatingCard>
  );

  const TeamsTab = () => {
    // Build display list: organizer sees full registrations; others see what loaded (may be empty if only player)
    const displayList = Array.isArray(registrations) ? registrations : [];
    return (
      <FloatingCard variant="secondary" size="large" className="delay-2">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
          <h3 className="form-section-title" style={{ margin: 0 }}>
            🏅 Registered Participants ({displayList.length})
          </h3>
        </div>

        {/* Organizer: Invite + Pending as responsive 2-column layout */}
        {isOrganizer && (
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-start', marginBottom: '1.5rem' }}>

            {/* Left: Invite Team search */}
            <div style={{ flex: '1 1 260px', minWidth: 0, padding: "1rem", background: "rgba(99,102,241,0.08)", border: "1px solid rgba(99,102,241,0.25)", borderRadius: "0.5rem" }}>
              <div style={{ fontWeight: 600, color: "#a78bfa", marginBottom: "0.6rem", fontSize: "0.9rem" }}>🔍 Invite a Team</div>
              <div style={{ position: "relative" }}>
                <input
                  key="invite-search-input"
                  type="text"
                  placeholder={`Search ${tournament?.sport_name || ""} teams by name…`}
                  value={inviteSearch}
                  onChange={(e) => handleInviteTeamSearch(e.target.value)}
                  autoComplete="off"
                  style={{ width: "100%", padding: "0.55rem 0.85rem", background: "rgba(15,23,42,0.7)", border: "1px solid rgba(99,102,241,0.4)", borderRadius: "0.4rem", color: "#e2e8f0", fontSize: "0.9rem", boxSizing: "border-box", outline: "none" }}
                />
                {inviteSearching && (
                  <span style={{ position: "absolute", right: "0.8rem", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: "0.8rem" }}>…</span>
                )}
              </div>
              {inviteResults.length > 0 && (
                <ul style={{ margin: "0.4rem 0 0", padding: 0, listStyle: "none", background: "rgba(15,23,42,0.9)", border: "1px solid rgba(99,102,241,0.3)", borderRadius: "0.4rem", maxHeight: "220px", overflowY: "auto" }}>
                  {inviteResults.map((team) => {
                    const isRegistered = registrations.some((r) => (r.team_id ?? r.user_id) === team.id);
                    return (
                    <li key={team.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.5rem 0.85rem", borderBottom: "1px solid rgba(99,102,241,0.15)", cursor: "default", gap: "0.6rem" }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ color: "#e2e8f0", fontSize: "0.88rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>{team.name}</span>
                        {isRegistered && (
                          <div style={{ fontSize: "0.75rem", color: "#22c55e", marginTop: "0.15rem" }}>✅ Already registered</div>
                        )}
                        {!isRegistered && team._sportMismatch && (
                          <div style={{ fontSize: "0.75rem", color: "#f59e0b", marginTop: "0.15rem" }}>
                            ⚠️ Different sport — {team.sport_name || "other"}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => handleInviteTeam(team.id, team.name, team._sportMismatch)}
                        disabled={invitingTeamId === team.id || isRegistered}
                        style={{ flexShrink: 0, padding: "0.25rem 0.7rem", background: isRegistered ? "rgba(100,116,139,0.2)" : team._sportMismatch ? "rgba(245,158,11,0.2)" : "rgba(99,102,241,0.25)", border: `1px solid ${isRegistered ? "rgba(100,116,139,0.4)" : team._sportMismatch ? "#f59e0b" : "rgba(99,102,241,0.6)"}`, color: isRegistered ? "#64748b" : team._sportMismatch ? "#f59e0b" : "#a78bfa", borderRadius: "0.3rem", cursor: isRegistered ? "not-allowed" : "pointer", fontSize: "0.78rem" }}
                      >
                        {invitingTeamId === team.id ? "Sending…" : isRegistered ? "Registered" : team._sportMismatch ? "Invite Anyway" : "Invite"}
                      </button>
                    </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* Right: Pending approvals panel */}
            {(() => {
              const pendingRegs = displayList.filter((r) => r.registration_status === "pending");
              return (
                <div style={{ flex: '1 1 260px', minWidth: 0, padding: "1rem", background: "rgba(251,191,36,0.06)", border: "1px solid rgba(251,191,36,0.2)", borderRadius: "0.5rem" }}>
                  <div style={{ fontWeight: 600, color: "#fbbf24", marginBottom: "0.6rem", fontSize: "0.9rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    📬 Pending Approvals
                    {pendingRegs.length > 0 && (
                      <span style={{ background: "#fbbf24", color: "#0f172a", borderRadius: "9999px", padding: "0.1rem 0.45rem", fontSize: "0.72rem", fontWeight: 700 }}>{pendingRegs.length}</span>
                    )}
                  </div>
                  {pendingRegs.length === 0 ? (
                    <div style={{ color: "#64748b", fontSize: "0.85rem" }}>No pending approvals.</div>
                  ) : (
                    <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                      {pendingRegs.map((reg) => (
                        <li key={reg.id} style={{ paddingBottom: "0.6rem", marginBottom: "0.6rem", borderBottom: "1px solid rgba(251,191,36,0.12)" }}>
                          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "0.4rem", flexWrap: "wrap" }}>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div style={{ color: "#e2e8f0", fontWeight: 600, fontSize: "0.88rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {reg.team_name || `${reg.first_name || ""} ${reg.last_name || ""}`.trim() || reg.username || "—"}
                              </div>
                              <div style={{ fontSize: "0.75rem", color: "#94a3b8" }}>{formatDateTime(reg.registered_at)}</div>
                            </div>
                            <div style={{ display: "flex", gap: "0.35rem", flexShrink: 0 }}>
                              <button
                                onClick={() => handleRegistrationStatus(reg.id, "approved")}
                                style={{ padding: "0.25rem 0.5rem", background: "rgba(34,197,94,0.2)", border: "1px solid #22c55e", color: "#22c55e", borderRadius: "0.35rem", cursor: "pointer", fontSize: "0.78rem" }}
                              >✅</button>
                              <button
                                onClick={() => handleRegistrationStatus(reg.id, "rejected")}
                                style={{ padding: "0.25rem 0.5rem", background: "rgba(239,68,68,0.2)", border: "1px solid #ef4444", color: "#ef4444", borderRadius: "0.35rem", cursor: "pointer", fontSize: "0.78rem" }}
                              >❌</button>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })()}

          </div>
        )}

        {/* Current user's registration status banner */}
        {userRegistration && (
          <div style={{ padding: "0.75rem 1rem", background: "rgba(99,102,241,0.15)", border: "1px solid rgba(99,102,241,0.4)", borderRadius: "0.5rem", marginBottom: "1rem", display: "flex", alignItems: "center", gap: "0.6rem" }}>
            <span>🎟️</span>
            <span style={{ color: "#e2e8f0" }}>
              Your registration:{" "}
              <strong style={{ color: userRegistration.registration_status === "approved" ? "#22c55e" : "#f59e0b" }}>
                {formatStatus(userRegistration.registration_status)}
              </strong>
              {userRegistration.team_name && <span style={{ color: "#a78bfa" }}> — {userRegistration.team_name}</span>}
              {entryFee && userRegistration.payment_status && (
                <span data-testid="my-entry-fee" style={{ color: userRegistration.payment_status === "paid" ? "#7dd3fc" : "#fcd34d" }}>
                  {" · "}Entry fee {entryFee}: {userRegistration.payment_status === "paid" ? "paid" : userRegistration.payment_status === "refunded" ? "refunded" : "unpaid"}
                </span>
              )}
            </span>
          </div>
        )}

        {isOrganizer && entryFee && (
          <PaidSummary
            statuses={displayList
              .filter((r) => !["rejected", "withdrawn"].includes(r.registration_status))
              .map((r) => r.payment_status)}
            remindUrl={paymentsSetup.enabled && !["completed", "cancelled"].includes(tournament.status) ? `/tournaments/${tournament.id}/payment-reminders` : null}
            note="Unpaid entries stay in until you remove them. Reminders go out at most once every 12 hours."
          />
        )}

        {displayList.length === 0 ? (
          <p style={{ color: "#94a3b8", textAlign: "center", padding: "2rem 0" }}>
            No participants registered yet.
          </p>
        ) : (
          <div style={{ maxHeight: "55vh", overflowY: "auto", overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead style={{ position: "sticky", top: 0, background: "rgba(15,20,30,0.97)", zIndex: 1 }}>
                <tr style={{ borderBottom: "2px solid rgba(99,102,241,0.3)" }}>
                  <th style={thStyle}>#</th>
                  <th style={thStyle}>Player / Team</th>
                  <th style={thStyle}>Type</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Registered</th>
                  {(isOrganizer || userRegistration) && <th style={thStyle}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {displayList.map((reg, idx) => {
                  const isOwnReg = user && reg.user_id === user.id;
                  const canWithdraw = isOwnReg && !isOrganizer &&
                    ["draft", "registration_open"].includes(tournament?.status);
                  return (
                  <tr key={reg.id} style={rowStyle}>
                    <td style={tdStyle({ color: "#64748b" })}>{idx + 1}</td>
                    <td style={tdStyle()}>
                      <div style={{ fontWeight: 600 }}>
                        {reg.team_name ? (
                          <span
                            onClick={() => reg.team_id ? navigate(`/teams/${reg.team_short_code || reg.team_uuid || reg.team_id}`) : undefined}
                            style={reg.team_id ? { cursor: "pointer", color: "#a78bfa", textDecoration: "underline", textDecorationStyle: "dotted" } : undefined}
                          >
                            {reg.team_name}
                          </span>
                        ) : (
                          <span
                            style={reg.user_id ? { cursor: 'pointer', color: '#b8acff' } : undefined}
                            onClick={reg.user_id ? () => navigate(`/players/${reg.user_short_code || reg.user_uuid || reg.user_id}`) : undefined}
                          >
                            {reg.first_name && reg.last_name ? `${reg.first_name} ${reg.last_name}` : reg.username || "—"}
                          </span>
                        )}
                      </div>
                      {reg.team_name && reg.username && (
                        <div style={{ color: "#94a3b8", fontSize: "0.8rem" }}>by {reg.username}</div>
                      )}
                    </td>
                    <td style={tdStyle({ color: "#94a3b8" })}>{formatStatus(reg.registration_type || "individual")}</td>
                    <td style={tdStyle()}>
                      <StatusBadge status={reg.registration_status} />
                      {isOrganizer && entryFee && (
                        <span style={{ marginLeft: "0.4rem", fontSize: "0.75rem", color: reg.payment_status === "paid" ? "#7dd3fc" : "#fcd34d" }}>
                          {reg.payment_status === "paid" ? "Paid" : reg.payment_status === "refunded" ? "Refunded" : "Unpaid"}
                        </span>
                      )}
                    </td>
                    <td style={tdStyle({ color: "#64748b", fontSize: "0.85rem" })}>{formatDateTime(reg.registered_at)}</td>
                    {(isOrganizer || userRegistration) && (
                      <td style={tdStyle()}>
                        {isOrganizer && reg.registration_status === "pending" && (
                          <div style={{ display: "flex", gap: "0.4rem" }}>
                            <button onClick={() => handleRegistrationStatus(reg.id, "approved")}
                              style={{ padding: "0.25rem 0.6rem", background: "rgba(34,197,94,0.2)", border: "1px solid #22c55e", color: "#22c55e", borderRadius: "0.35rem", cursor: "pointer", fontSize: "0.78rem" }}>
                              ✅ Approve
                            </button>
                            <button onClick={() => handleRegistrationStatus(reg.id, "rejected")}
                              style={{ padding: "0.25rem 0.6rem", background: "rgba(239,68,68,0.2)", border: "1px solid #ef4444", color: "#ef4444", borderRadius: "0.35rem", cursor: "pointer", fontSize: "0.78rem" }}>
                              ❌ Reject
                            </button>
                          </div>
                        )}
                        {isOrganizer && reg.registration_status !== "pending" && (
                          tournament?.status === "in_progress" ? (
                            <span style={{ color: "#64748b", fontSize: "0.75rem", fontStyle: "italic" }}>🔒 Tournament started</span>
                          ) : (
                            <button onClick={() => setRemoveConfirmReg(reg)}
                              style={{ padding: "0.25rem 0.6rem", background: "rgba(239,68,68,0.15)", border: "1px solid #ef4444", color: "#ef4444", borderRadius: "0.35rem", cursor: "pointer", fontSize: "0.78rem" }}>
                              Remove
                            </button>
                          )
                        )}
                        {canWithdraw && (
                          tournament?.status === "in_progress" ? (
                            <span style={{ color: "#64748b", fontSize: "0.75rem", fontStyle: "italic" }}>🔒 Tournament started</span>
                          ) : (
                            <button onClick={() => setRemoveConfirmReg(reg)}
                              style={{ padding: "0.25rem 0.6rem", background: "rgba(251,191,36,0.15)", border: "1px solid #fbbf24", color: "#fbbf24", borderRadius: "0.35rem", cursor: "pointer", fontSize: "0.78rem" }}>
                              Withdraw
                            </button>
                          )
                        )}
                      </td>
                    )}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </FloatingCard>
    );
  };

  const ScheduleTab = () => {
    const seedingAllowed = ["registration_closed"].includes(tournament?.status);
    const approvedCount = registrations.filter((r) => r.registration_status === "approved").length;
    return (
    <FloatingCard variant="secondary" size="large" className="delay-2">
      <h3 className="form-section-title" style={{ marginBottom: "1rem" }}>⚔️ Match Schedule / Seeding</h3>

      {!isOrganizer ? (
        <p style={{ color: "#94a3b8", textAlign: "center", padding: "1.5rem 0" }}>
          Only the tournament organizer can generate matches.
        </p>
      ) : tournament?.status === "in_progress" ? (
        <div style={{ padding: "1rem", background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: "0.5rem", color: "#f87171" }}>
          <strong>🔒 Schedule locked</strong>
          <p style={{ margin: "0.4rem 0 0", fontSize: "0.88rem", color: "#fca5a5" }}>
            The tournament is in progress. Matches cannot be regenerated once play has begun.
          </p>
        </div>
      ) : !seedingAllowed ? (
        <div style={{ padding: "1rem", background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.3)", borderRadius: "0.5rem", color: "#f59e0b" }}>
          <strong>⏳ Seeding not yet available</strong>
          <p style={{ margin: "0.4rem 0 0", fontSize: "0.88rem" }}>
            Close registration first. Schedule can be generated once registration is closed
            so all participants are known before matches begin.
          </p>
        </div>
      ) : (
        <>
          {/* Format info card */}
          <div style={{ padding: "1rem", background: "rgba(99,102,241,0.08)", border: "1px solid rgba(99,102,241,0.25)", borderRadius: "0.5rem", marginBottom: "1.5rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.4rem" }}>
              <span style={{ fontSize: "1.1rem" }}>{SCHEDULE_FORMAT_INFO[tournament?.tournament_type]?.icon || "⚔️"}</span>
              <strong style={{ color: "#a78bfa" }}>{SCHEDULE_FORMAT_INFO[tournament?.tournament_type]?.label || formatStatus(tournament?.tournament_type)}</strong>
            </div>
            <p style={{ color: "#94a3b8", fontSize: "0.88rem", margin: 0, lineHeight: 1.6 }}>
              {SCHEDULE_FORMAT_INFO[tournament?.tournament_type]?.description}
            </p>
          </div>

          {tournament?.status === "registration_closed" && (
            <p style={{ color: "#22c55e", fontSize: "0.88rem", marginBottom: "1rem" }}>
              ✅ Registration is closed — ready to seed. Matches won't be startable until the tournament is started.
            </p>
          )}

          {/* Stats */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "1rem", marginBottom: "1.5rem" }}>
            {[
              ["✅ Approved",  approvedCount],
              ["⏳ Pending",   registrations.filter((r) => r.registration_status === "pending").length],
              ["⚔️ Matches",  matches.length],
            ].map(([label, count]) => (
              <div key={label} style={{ padding: "1rem", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(99,102,241,0.2)", borderRadius: "0.5rem", textAlign: "center" }}>
                <div style={{ fontSize: "1.6rem", fontWeight: 700, color: "#a78bfa" }}>{count}</div>
                <div style={{ color: "#94a3b8", fontSize: "0.85rem" }}>{label}</div>
              </div>
            ))}
          </div>

          {approvedCount < 2 && (
            <p style={{ color: "#f87171", fontSize: "0.85rem", marginBottom: "0.75rem" }}>
              Need at least 2 approved participants to generate a schedule.
            </p>
          )}

          {/* Bye / auto-advance banner */}
          {byeAdvancers.length > 0 && (
            <div style={{ padding: "0.75rem 1rem", background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.3)", borderRadius: "0.5rem", marginBottom: "1rem" }}>
              <strong style={{ color: "#22c55e", fontSize: "0.88rem" }}>🚀 Auto-advancing (bye):</strong>
              <span style={{ color: "#e2e8f0", fontSize: "0.88rem", marginLeft: "0.5rem" }}>
                {byeAdvancers.map((b) => b.name).join(", ")} will skip Round 1 and advance directly to Round 2.
              </span>
            </div>
          )}

          {/* Action buttons */}
          <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", alignItems: "center" }}>
            <FloatingButton
              variant="success"
              size="medium"
              onClick={handleGenerateSchedule}
              disabled={generatingSchedule || approvedCount < 2}
              icon="⚔️"
            >
              <span className="btn-text">
                {generatingSchedule ? "Generating…" : matches.length > 0 ? "Clear & Regenerate" : "Generate Schedule"}
              </span>
            </FloatingButton>

            <FloatingButton
              variant="outline"
              size="medium"
              onClick={() => {
                setCustomPairs([{ p1: "", p2: "" }]);
                setShowCustomModal(true);
              }}
              disabled={approvedCount < 2}
              icon="✏️"
            >
              <span className="btn-text">Custom Schedule</span>
            </FloatingButton>
          </div>

          {matches.length > 0 && (
            <p style={{ color: "#64748b", fontSize: "0.82rem", marginTop: "0.6rem" }}>
              ℹ️ {matches.length} {matches.length === 1 ? "match" : "matches"} already scheduled. "Clear &amp; Regenerate" will delete them all and create a fresh bracket.
            </p>
          )}
        </>
      )}
    </FloatingCard>
    );
  };

  const MatchesTab = () => {
    // ── Next-round computation ──────────────────────────────────────────────
    const isSingleElim = tournament?.tournament_type === "single_elimination";
    const isProgressive = PROGRESSIVE_FORMATS.includes(tournament?.tournament_type);
    const canAdvance =
      isOrganizer && isProgressive && tournament?.status === "in_progress" && !!progress?.canAdvance;
    const rounds = [...new Set(matches.map((m) => m.round ?? m.round_number ?? 1))].sort((a, b) => a - b);
    const maxRound = rounds.length ? Math.max(...rounds) : 0;
    const maxRoundMatches = matches.filter((m) => (m.round ?? 1) === maxRound);
    const allMaxRoundComplete = maxRoundMatches.length > 0 && maxRoundMatches.every((m) => m.status === "completed");
    // Total rounds in a single-elim bracket
    const approvedCount = registrations.filter((r) => r.registration_status === "approved").length;
    const slots = approvedCount > 0 ? (() => { let p = 1; while (p < approvedCount) p *= 2; return p; })() : 0;
    const totalRounds = slots > 0 ? Math.ceil(Math.log2(slots)) : 0;
    const nextRoundExists = matches.some((m) => (m.round ?? 1) === maxRound + 1);
    const canGenerateNextRound =
      canAdvanceBracket &&
      tournament?.status === "in_progress" &&
      isSingleElim &&
      allMaxRoundComplete &&
      !nextRoundExists &&
      maxRound < totalRounds;

    // Tournament winner from the completed final match
    const finalMatch =
      isSingleElim && allMaxRoundComplete && maxRoundMatches.length === 1 && maxRound === totalRounds
        ? maxRoundMatches[0]
        : null;
    const tournamentWinnerName = isProgressive
      ? (progress?.champion?.name || null)
      : finalMatch
      ? (() => {
          const wId = finalMatch.winnerId ?? finalMatch.winner_id;
          if (!wId) return null;
          const isP1 = wId === (finalMatch.participant1Id ?? finalMatch.participant_1_id);
          return isP1
            ? (finalMatch.participant1Name || finalMatch.participant_1_name || null)
            : (finalMatch.participant2Name || finalMatch.participant_2_name || null);
        })()
      : null;

    return (
    <FloatingCard variant="secondary" size="large" className="delay-2">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem", flexWrap: "wrap", gap: "0.5rem" }}>
        <h3 className="form-section-title" style={{ margin: 0 }}>🎮 Matches ({matches.length})</h3>
        <FloatingButton variant="outline" size="small" onClick={loadMatches} icon="🔄">
          <span className="btn-text">Refresh</span>
        </FloatingButton>
      </div>

      {/* ── Winner Banner ── */}
      {tournamentWinnerName && (
        <div style={{ padding: "1.25rem 1.5rem", background: "linear-gradient(135deg, rgba(250,204,21,0.15), rgba(245,158,11,0.1))", border: "1px solid rgba(250,204,21,0.4)", borderRadius: "0.6rem", marginBottom: "1rem", textAlign: "center" }}>
          <div style={{ fontSize: "2rem", marginBottom: "0.4rem" }}>🏆</div>
          <div style={{ color: "#fbbf24", fontWeight: 700, fontSize: "1.1rem" }}>Tournament Winner</div>
          <div style={{ color: "#fde68a", fontWeight: 600, fontSize: "1.3rem", marginTop: "0.25rem" }}>{tournamentWinnerName}</div>
        </div>
      )}

      {/* ── Next Round Banner ── */}
      {canGenerateNextRound && (
        <div style={{ padding: "1rem", background: "rgba(99,102,241,0.12)", border: "1px solid rgba(99,102,241,0.4)", borderRadius: "0.5rem", marginBottom: "1rem", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap" }}>
          <div>
            <strong style={{ color: "#a78bfa" }}>🏆 Round {maxRound} complete!</strong>
            <p style={{ color: "#94a3b8", fontSize: "0.88rem", margin: "0.25rem 0 0" }}>
              All {maxRoundMatches.length} {maxRoundMatches.length === 1 ? "match" : "matches"} finished.
              {maxRound + 1 === totalRounds ? " 🏆 Generate the Final!" : ` Generate Round ${maxRound + 1} to continue.`}
            </p>
          </div>
          <FloatingButton
            variant="success"
            size="small"
            icon="⚔️"
            onClick={() => handleGenerateNextRound(maxRound)}
            disabled={generatingNextRound}
          >
            {generatingNextRound ? "Generating…" : maxRound + 1 === totalRounds ? "Generate Final" : `Generate Round ${maxRound + 1}`}
          </FloatingButton>
        </div>
      )}

      {isOrganizer && tournament?.status === "in_progress" && tournament?.tournament_type !== "round_robin" &&
        !progress?.complete && tournament?.bracket_settings?.auto_advance !== false && (
        <p style={{ color: "#64748b", fontSize: "0.82rem", margin: "0 0 0.75rem" }}>
          ⚡ Next matches are created automatically as results come in. Use the button below if any are missing.
        </p>
      )}

      {/* ── Advance banner (double elimination / Swiss / groups + knockout) ── */}
      {canAdvance && (
        <div style={{ padding: "1rem", background: "rgba(99,102,241,0.12)", border: "1px solid rgba(99,102,241,0.4)", borderRadius: "0.5rem", marginBottom: "1rem", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap" }}>
          <div>
            <strong style={{ color: "#a78bfa" }}>
              {progress.format === "swiss_system"
                ? `🏆 Round ${progress.currentRound} complete!`
                : progress.format === "group_knockout" && progress.stage === "knockout" && !matches.some((m) => (m.matchConfig || m.match_config || {}).bracket_type === "knockout")
                ? "🏆 Group stage complete!"
                : "🏆 New matches are ready"}
            </strong>
            <p style={{ color: "#94a3b8", fontSize: "0.88rem", margin: "0.25rem 0 0" }}>
              Next: {(progress.nextMatches || []).join(", ")}
              {progress.nextBye ? ` · Bye: ${progress.nextBye.name}` : ""}
            </p>
          </div>
          <FloatingButton variant="success" size="small" icon="⚔️" onClick={handleAdvanceBracket} disabled={generatingNextRound}>
            {generatingNextRound ? "Generating…" : progress.format === "swiss_system" ? `Pair Round ${progress.nextRound}` : "Create Next Matches"}
          </FloatingButton>
        </div>
      )}

      {isProgressive && !canAdvance && !progress?.complete && progress?.needsWinner?.length > 0 && (
        <div style={{ padding: "0.75rem 1rem", background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.4)", borderRadius: "0.5rem", marginBottom: "1rem", color: "#fbbf24", fontSize: "0.88rem" }}>
          ⚠️ {progress.needsWinner.length} completed {progress.needsWinner.length === 1 ? "match has" : "matches have"} no winner. Elimination matches need a winner before the bracket can continue.
        </div>
      )}

      {matches.length === 0 ? (
        <div style={{ textAlign: "center", padding: "2rem 0" }}>
          <div style={{ fontSize: "2.5rem", marginBottom: "0.75rem" }}>⚔️</div>
          <p style={{ color: "#94a3b8" }}>No matches yet.</p>
          {isOrganizer && (
            <p style={{ color: "#64748b", fontSize: "0.85rem" }}>
              Go to the Schedule tab to generate matches.
            </p>
          )}
        </div>
      ) : (
        <TournamentBracket
          matches={matches}
          tournamentType={tournament?.tournament_type}
          registrations={registrations}
          byeAdvancers={byeAdvancers}
          progress={progress}
          onMatchClick={(m) => navigate(`/matches/${eid(m)}`, { state: { from: `/tournaments/${id}`, tab: "matches" } })}
        />
      )}
    </FloatingCard>
    );
  };

  const StandingsTable = ({ rows, columns, highlight }) => (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ borderBottom: "2px solid rgba(99,102,241,0.3)" }}>
            {["Rank", "Participant", ...columns.map((c) => c.label)].map((h) => (
              <th key={h} style={{ ...thStyle, textAlign: h === "Participant" ? "left" : "center" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.type}:${r.id}`} style={{ ...rowStyle, background: highlight(r) ? "rgba(34,197,94,0.07)" : "transparent" }}>
              <td style={{ ...tdStyle({ textAlign: "center" }), fontWeight: 700, color: r.rank === 1 ? "#fbbf24" : "#94a3b8" }}>{r.rank}</td>
              <td style={tdStyle({ fontWeight: 600 })}>
                {r.name}
                {highlight(r) && <span style={{ color: "#22c55e", fontSize: "0.75rem", marginLeft: "0.4rem" }}>✓ qualifies</span>}
                {(r.withdrawn || progress?.withdrawn?.some((w) => w.id === r.id && w.type === r.type)) && (
                  <span style={{ color: "#f87171", fontSize: "0.75rem", marginLeft: "0.4rem" }}>withdrew</span>
                )}
              </td>
              {columns.map((c) => (
                <td key={c.key} style={tdStyle({ textAlign: "center", color: c.key === "points" ? "#a78bfa" : "#94a3b8", fontWeight: c.key === "points" ? 700 : 400 })}>{r[c.key]}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const ProgressStandings = () => {
    if (progress.format === "swiss_system" && progress.standings) {
      return (
        <>
          <p style={{ color: "#94a3b8", fontSize: "0.85rem", marginTop: 0 }}>
            Round {progress.currentRound} of {progress.totalRounds}. Win 1 point, draw ½, bye 1. Ties split by Buchholz (opponents' points).
          </p>
          <StandingsTable
            rows={progress.standings}
            highlight={() => false}
            columns={[
              { key: "played", label: "P" }, { key: "won", label: "W" }, { key: "drawn", label: "D" },
              { key: "lost", label: "L" }, { key: "byes", label: "Bye" }, { key: "buchholz", label: "Buch." }, { key: "points", label: "Pts" },
            ]}
          />
        </>
      );
    }
    if (progress.format === "round_robin" && progress.standings) {
      return (
        <>
          <p style={{ color: "#94a3b8", fontSize: "0.85rem", marginTop: 0 }}>
            Win 3 points, draw 1. Ties split by score difference, then score for, then head-to-head.
          </p>
          <StandingsTable
            rows={progress.standings}
            highlight={() => false}
            columns={[
              { key: "played", label: "P" }, { key: "won", label: "W" }, { key: "drawn", label: "D" },
              { key: "lost", label: "L" }, { key: "score_for", label: "F" }, { key: "score_against", label: "A" },
              { key: "score_difference", label: "+/-" }, { key: "points", label: "Pts" },
            ]}
          />
        </>
      );
    }
    if (progress.format === "group_knockout" && progress.groups) {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
          {progress.groups.map((g) => (
            <div key={g.name}>
              <h4 style={{ color: "#e2e8f0", margin: "0 0 0.5rem" }}>Group {g.name}</h4>
              <StandingsTable
                rows={g.standings}
                highlight={(r) => r.qualifies}
                columns={[
                  { key: "played", label: "P" }, { key: "won", label: "W" }, { key: "drawn", label: "D" },
                  { key: "lost", label: "L" }, { key: "score_difference", label: "+/-" }, { key: "points", label: "Pts" },
                ]}
              />
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const StandingsTab = () => (
    <FloatingCard variant="secondary" size="large" className="delay-2">
      <h3 className="form-section-title" style={{ marginBottom: "1rem" }}>🏆 Standings</h3>

      {(tournament?.champion_name || progress?.champion) && (
        <p style={{ color: "#fbbf24", fontWeight: 700, marginTop: 0 }}>🏆 Champion: {tournament?.champion_name || progress.champion.name}</p>
      )}

      {progress && (progress.standings || progress.groups) ? (
        <ProgressStandings />
      ) : standings.length === 0 ? (
        <div style={{ textAlign: "center", padding: "2rem 0" }}>
          <div style={{ fontSize: "2.5rem", marginBottom: "0.75rem" }}>🏅</div>
          <p style={{ color: "#94a3b8" }}>Standings will appear once matches are completed.</p>
        </div>
      ) : (
        <div style={{ maxHeight: "55vh", overflowY: "auto", overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid rgba(99,102,241,0.3)" }}>
                {["Rank", "Participant", "P", "W", "D", "L", "Pts"].map((h) => (
                  <th key={h} style={{ ...thStyle, textAlign: h === "Participant" ? "left" : "center" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {standings.map((s, idx) => (
                <tr key={s.id} style={{ ...rowStyle, background: idx === 0 ? "rgba(250,204,21,0.06)" : "transparent" }}>
                  <td style={{ ...tdStyle({ textAlign: "center" }), color: idx === 0 ? "#fbbf24" : idx === 1 ? "#94a3b8" : idx === 2 ? "#cd7c2f" : "#64748b", fontWeight: 700 }}>
                    {idx === 0 ? "🥇" : idx === 1 ? "🥈" : idx === 2 ? "🥉" : idx + 1}
                  </td>
                  <td style={tdStyle({ fontWeight: 600 })}>{s.name}</td>
                  {[s.played, s.won, s.drawn, s.lost].map((v, i) => (
                    <td key={i} style={tdStyle({ textAlign: "center", color: "#94a3b8" })}>{v}</td>
                  ))}
                  <td style={tdStyle({ textAlign: "center", color: "#a78bfa", fontWeight: 700 })}>{s.points}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </FloatingCard>
  );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <GameLayout onLogout={() => {}} profile={user} showFloatingNav>
      <div className="tournaments-gaming-layout">

        {/* ── Hero Header ── */}
        <div className="tournaments-header-gaming">
          <FloatingCard variant="primary" size="large" className="delay-1">
            <div className="header-content-gaming">
              <div className="title-section-gaming">
                <h1 className="tournaments-title-gaming">🏆 {tournament.name}</h1>
                <p className="tournaments-subtitle-gaming">
                  {tournament.sport_name && `${tournament.sport_name} · `}
                  {formatStatus(tournament.tournament_type)}
                </p>
                <StatusBadge status={tournament.status} />
              </div>

              <div className="header-actions-gaming">
                {isOrganizer && tournament.status === "draft" && (
                  <FloatingButton variant="success" size="medium" onClick={handleOpenRegistration} icon="📣" className="delay-2">
                    <span className="btn-text">Open Registration</span>
                  </FloatingButton>
                )}
                {isOrganizer && tournament.status === "registration_open" && (
                  <FloatingButton variant="warning" size="medium" onClick={() => handleStatusChange("registration_closed")} icon="🔒" className="delay-2">
                    <span className="btn-text">Close Registration</span>
                  </FloatingButton>
                )}
                {isOrganizer && tournament.status === "registration_closed" && (
                  <FloatingButton variant="success" size="medium" onClick={() => handleStatusChange("in_progress")} icon="🚀" className="delay-2">
                    <span className="btn-text">Start Tournament</span>
                  </FloatingButton>
                )}
                {isOrganizer && tournament.status === "in_progress" && (
                  <FloatingButton variant="primary" size="medium" onClick={() => handleStatusChange("completed")} icon="🏁" className="delay-2">
                    <span className="btn-text">Complete Tournament</span>
                  </FloatingButton>
                )}
                {canRegister && (
                  <FloatingButton variant="success" size="medium" onClick={handleRegister} disabled={registering} icon="✅" className="delay-2">
                    <span className="btn-text">
                      {registering ? "Registering…" : (tournament.sport_type === "team" ? "Register Team" : "Register Now")}
                    </span>
                  </FloatingButton>
                )}
                {feeDue(userRegistration) && (
                  <FloatingButton variant="success" size="medium" onClick={startEntryPayment} disabled={paying} icon="💳" className="delay-2">
                    <span className="btn-text">{paying ? "Opening payment…" : `Pay entry fee ${entryFee}`}</span>
                  </FloatingButton>
                )}
                {isOrganizer && entryFeeCents > 0 && paymentsSetup.enabled && (
                  <HostPaymentsLink style={{ flexBasis: "100%" }} />
                )}
                {entryFeeCents > 0 && (canRegister || feeDue(userRegistration)) && (
                  <RefundPolicyNote policy={tournament.refund_policy} style={{ flexBasis: "100%" }} />
                )}
                {userRegistration && (
                  <span style={{ display: "flex", alignItems: "center", gap: "0.4rem", padding: "0.5rem 0.9rem", background: "rgba(34,197,94,0.15)", border: "1px solid #22c55e", borderRadius: "2rem", color: "#22c55e", fontSize: "0.85rem", fontWeight: 600 }}>
                    ✅ {userRegistration.team_name ? userRegistration.team_name : "Registered"}
                  </span>
                )}
                {isOrganizer && !["completed", "cancelled"].includes(tournament?.status) && (
                  <FloatingButton variant="secondary" size="medium" onClick={() => navigate(`/tournaments/${id}/edit`)} icon="✏️" className="delay-3">
                    <span className="btn-text">Edit</span>
                  </FloatingButton>
                )}
                {isOrganizer && tournament.status !== "cancelled" && tournament.status !== "completed" && (
                  <FloatingButton variant="warning" size="medium" onClick={() => handleStatusChange("cancelled")} icon="⛔" className="delay-3">
                    <span className="btn-text">Cancel</span>
                  </FloatingButton>
                )}
                {isOrganizer && (
                  <FloatingButton variant="danger" size="medium" onClick={() => setShowDeleteConfirm(true)} disabled={deleting} icon="🗑️" className="delay-4">
                    <span className="btn-text">Delete</span>
                  </FloatingButton>
                )}
                <FloatingButton variant="outline" size="medium" onClick={() => navigate("/tournaments")} icon="←" className="delay-4">
                  <span className="btn-text">Back</span>
                </FloatingButton>
              </div>
            </div>
          </FloatingCard>
        </div>

        {/* ── Alerts ── */}
        {error && (
          <div className="gaming-message-floating error">
            <span className="message-icon">❌</span>
            <span className="message-text">{error}</span>
            <button onClick={() => setError("")} style={{ marginLeft: "auto", background: "none", border: "none", color: "#f87171", cursor: "pointer", fontSize: "1.1rem" }}>×</button>
          </div>
        )}
        {success && (
          <div className="gaming-message-floating success">
            <span className="message-icon">✅</span>
            <span className="message-text">{success}</span>
            <button onClick={() => setSuccess("")} style={{ marginLeft: "auto", background: "none", border: "none", color: "#22c55e", cursor: "pointer", fontSize: "1.1rem" }}>×</button>
          </div>
        )}

        {/* ── Tab Navigation ── */}
        <div style={{ display: "flex", gap: "0.25rem", padding: "0.25rem", background: "rgba(0,0,0,0.25)", borderRadius: "0.75rem", flexWrap: "wrap" }}>
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                flex: 1,
                minWidth: "110px",
                padding: "0.65rem 0.9rem",
                borderRadius: "0.55rem",
                border: "none",
                cursor: "pointer",
                fontWeight: activeTab === tab.key ? 700 : 500,
                fontSize: "0.85rem",
                transition: "all 0.15s",
                background: activeTab === tab.key ? "rgba(99,102,241,0.35)" : "transparent",
                color: activeTab === tab.key ? "#e2e8f0" : "#94a3b8",
                boxShadow: activeTab === tab.key ? "0 0 0 1px rgba(99,102,241,0.5)" : "none",
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Active Tab Content ── */}
        {activeTab === "overview"  && <OverviewTab />}
        {activeTab === "teams"     && TeamsTab()}
        {activeTab === "schedule"  && <ScheduleTab />}
        {activeTab === "matches"   && <MatchesTab />}
        {activeTab === "standings" && <StandingsTab />}

        {/* ── Delete Confirm Modal ── */}
        {showDeleteConfirm && (
          <div className="modal-overlay-gaming" role="dialog" aria-modal="true">
            <FloatingCard size="large" transparent={false} hover={false} className="modal-gaming" style={{ background: "rgba(10,14,24,0.98)", backdropFilter: "none", WebkitBackdropFilter: "none", color: "#e2e8f0" }}>
              <div className="modal-header">
                <h3>🗑️ Delete Tournament</h3>
                <button type="button" className="close-button" onClick={() => setShowDeleteConfirm(false)} aria-label="Close">×</button>
              </div>
              <div className="modal-body">
                <p style={{ color: "#e2e8f0", marginBottom: "0.5rem" }}>
                  Are you sure you want to delete <strong>{tournament.name}</strong>? This cannot be undone.
                </p>
                {registrations.some((r) => r.registration_status !== "rejected") && (
                  <p style={{ color: "#f87171", fontSize: "0.9rem" }}>
                    ⚠️ This tournament has registrations, so it cannot be deleted. Cancel it instead to keep the entrants informed.
                  </p>
                )}
              </div>
              <div className="modal-actions">
                <FloatingButton variant="secondary" size="small" onClick={() => setShowDeleteConfirm(false)} disabled={deleting}>Cancel</FloatingButton>
                <FloatingButton variant="danger" size="small" onClick={handleDelete} disabled={deleting} icon="🗑️">
                  {deleting ? "Deleting…" : "Delete"}
                </FloatingButton>
              </div>
            </FloatingCard>
          </div>
        )}

        {/* ── Remove / Withdraw Confirm Modal ── */}
        {removeConfirmReg && (
          <div className="modal-overlay-gaming" role="dialog" aria-modal="true">
            <FloatingCard size="large" transparent={false} hover={false} className="modal-gaming" style={{ background: "rgba(10,14,24,0.98)", backdropFilter: "none", WebkitBackdropFilter: "none", color: "#e2e8f0" }}>
              <div className="modal-header">
                <h3>{isOrganizer && removeConfirmReg.user_id !== user?.id ? "🗑️ Remove Registration" : "🚪 Withdraw Registration"}</h3>
                <button type="button" className="close-button" onClick={() => setRemoveConfirmReg(null)} aria-label="Close">×</button>
              </div>
              <div className="modal-body">
                <p style={{ color: "#e2e8f0", marginBottom: "0.5rem" }}>
                  Are you sure you want to {isOrganizer && removeConfirmReg.user_id !== user?.id ? "remove" : "withdraw"}{" "}
                  <strong>{removeConfirmReg.team_name || removeConfirmReg.username || "this registration"}</strong> from the tournament?
                </p>
                {removeConfirmReg.payment_status === "paid" && (
                  <p style={{ color: "#fcd34d", fontSize: "0.88rem", marginBottom: "0.5rem" }} data-testid="withdraw-refund-note">
                    {isOrganizer && removeConfirmReg.user_id !== user?.id
                      ? "Their entry fee is refunded automatically."
                      : leaveMessage({ what: "this tournament", paid: true, policy: tournament?.refund_policy })
                        .replace("Are you sure you want to leave this tournament? ", "")}
                  </p>
                )}
                <p style={{ color: "#f87171", fontSize: "0.88rem" }}>⚠️ This action cannot be undone.</p>
              </div>
              <div className="modal-actions">
                <FloatingButton variant="secondary" size="small" onClick={() => setRemoveConfirmReg(null)}>Cancel</FloatingButton>
                <FloatingButton
                  variant="danger"
                  size="small"
                  icon={isOrganizer && removeConfirmReg.user_id !== user?.id ? "🗑️" : "🚪"}
                  onClick={() => {
                    const reg = removeConfirmReg;
                    setRemoveConfirmReg(null);
                    handleRemoveRegistration(reg.id);
                  }}
                >
                  {isOrganizer && removeConfirmReg.user_id !== user?.id ? "Remove" : "Withdraw"}
                </FloatingButton>
              </div>
            </FloatingCard>
          </div>
        )}

        {/* ── Clear & Regenerate Confirm Modal ── */}
        {showClearConfirm && (
          <div className="modal-overlay-gaming" role="dialog" aria-modal="true">
            <FloatingCard size="large" transparent={false} hover={false} className="modal-gaming" style={{ background: "rgba(10,14,24,0.98)", backdropFilter: "none", WebkitBackdropFilter: "none", color: "#e2e8f0" }}>
              <div className="modal-header">
                <h3>🔄 Clear &amp; Regenerate</h3>
                <button type="button" className="close-button" onClick={() => setShowClearConfirm(false)} aria-label="Close">×</button>
              </div>
              <div className="modal-body">
                <p style={{ color: "#e2e8f0", marginBottom: "0.5rem" }}>
                  This will <strong>delete all {matches.length} existing {matches.length === 1 ? "match" : "matches"}</strong> and generate a fresh bracket from the current approved participants.
                </p>
                <p style={{ color: "#f87171", fontSize: "0.88rem" }}>⚠️ Any recorded scores will be permanently lost.</p>
              </div>
              <div className="modal-actions">
                <FloatingButton variant="secondary" size="small" onClick={() => setShowClearConfirm(false)}>Cancel</FloatingButton>
                <FloatingButton variant="warning" size="small" icon="🔄" onClick={handleClearMatches} disabled={generatingSchedule}>
                  {generatingSchedule ? "Generating…" : "Clear & Regenerate"}
                </FloatingButton>
              </div>
            </FloatingCard>
          </div>
        )}

        {/* ── Custom Schedule Builder Modal ── */}
        {showCustomModal && (() => {
          const approvedRegs = registrations.filter((r) => r.registration_status === "approved");
          const usedInPair = (pairIdx, side, val) => {
            // val already selected in the OTHER side of the same pair, or in any other pair slot
            if (!val) return false;
            return customPairs.some((p, i) => {
              if (i === pairIdx) return side === "p1" ? p.p2 === val : p.p1 === val;
              return p.p1 === val || p.p2 === val;
            });
          };
          return (
            <div className="modal-overlay-gaming" role="dialog" aria-modal="true">
              <FloatingCard size="large" transparent={false} hover={false} className="modal-gaming" style={{ background: "rgba(10,14,24,0.98)", backdropFilter: "none", WebkitBackdropFilter: "none", color: "#e2e8f0", maxWidth: "620px", maxHeight: "85vh", overflowY: "auto" }}>
                <div className="modal-header">
                  <h3>✏️ Custom Schedule</h3>
                  <button type="button" className="close-button" onClick={() => setShowCustomModal(false)} aria-label="Close">×</button>
                </div>
                <div className="modal-body">
                  <p style={{ color: "#94a3b8", fontSize: "0.88rem", marginBottom: "1rem" }}>
                    Manually pair teams for each match. Each team can only appear once per round.
                  </p>

                  <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                    {customPairs.map((pair, idx) => (
                      <div key={idx} style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.75rem", background: "rgba(99,102,241,0.07)", border: "1px solid rgba(99,102,241,0.2)", borderRadius: "0.45rem" }}>
                        <span style={{ color: "#64748b", fontSize: "0.78rem", minWidth: "28px", textAlign: "center" }}>#{idx + 1}</span>
                        <select
                          value={pair.p1}
                          onChange={(e) => setCustomPairs((prev) => prev.map((p, i) => i === idx ? { ...p, p1: e.target.value } : p))}
                          style={{ flex: 1, padding: "0.45rem 0.6rem", background: "rgba(15,23,42,0.9)", border: "1px solid rgba(99,102,241,0.4)", borderRadius: "0.35rem", color: "#e2e8f0", fontSize: "0.85rem" }}
                        >
                          <option value="">— Select team —</option>
                          {approvedRegs.map((r) => {
                            const val = String(r.team_id || r.user_id);
                            const label = r.team_name || `${r.first_name || ""} ${r.last_name || ""}`.trim() || r.username;
                            return (
                              <option key={val} value={val} disabled={usedInPair(idx, "p1", val) || pair.p2 === val}>
                                {label}
                              </option>
                            );
                          })}
                        </select>
                        <span style={{ color: "#64748b", fontWeight: 700, fontSize: "0.85rem" }}>vs</span>
                        <select
                          value={pair.p2}
                          onChange={(e) => setCustomPairs((prev) => prev.map((p, i) => i === idx ? { ...p, p2: e.target.value } : p))}
                          style={{ flex: 1, padding: "0.45rem 0.6rem", background: "rgba(15,23,42,0.9)", border: "1px solid rgba(99,102,241,0.4)", borderRadius: "0.35rem", color: "#e2e8f0", fontSize: "0.85rem" }}
                        >
                          <option value="">— Select team —</option>
                          {approvedRegs.map((r) => {
                            const val = String(r.team_id || r.user_id);
                            const label = r.team_name || `${r.first_name || ""} ${r.last_name || ""}`.trim() || r.username;
                            return (
                              <option key={val} value={val} disabled={usedInPair(idx, "p2", val) || pair.p1 === val}>
                                {label}
                              </option>
                            );
                          })}
                        </select>
                        {customPairs.length > 1 && (
                          <button
                            onClick={() => setCustomPairs((prev) => prev.filter((_, i) => i !== idx))}
                            style={{ background: "rgba(239,68,68,0.15)", border: "1px solid #ef4444", color: "#ef4444", borderRadius: "0.3rem", cursor: "pointer", padding: "0.3rem 0.5rem", fontSize: "0.8rem", flexShrink: 0 }}
                          >✕</button>
                        )}
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={() => setCustomPairs((prev) => [...prev, { p1: "", p2: "" }])}
                    style={{ marginTop: "0.75rem", width: "100%", padding: "0.5rem", background: "rgba(99,102,241,0.1)", border: "1px dashed rgba(99,102,241,0.4)", color: "#a78bfa", borderRadius: "0.4rem", cursor: "pointer", fontSize: "0.85rem" }}
                  >
                    + Add another match
                  </button>
                </div>
                <div className="modal-actions">
                  <FloatingButton variant="secondary" size="small" onClick={() => setShowCustomModal(false)} disabled={savingCustom}>Cancel</FloatingButton>
                  <FloatingButton
                    variant="success"
                    size="small"
                    icon="✅"
                    disabled={savingCustom || customPairs.every((p) => !p.p1 || !p.p2 || p.p1 === p.p2)}
                    onClick={handleSaveCustomSchedule}
                  >
                    {savingCustom ? "Creating…" : `Create ${customPairs.filter((p) => p.p1 && p.p2 && p.p1 !== p.p2).length} Match(es)`}
                  </FloatingButton>
                </div>
              </FloatingCard>
            </div>
          );
        })()}

        {/* ── Doubles Registration Modal (TN-B11) ── */}
        {showPartnerModal && (
          <div className="modal-overlay-gaming" role="dialog" aria-modal="true">
            <FloatingCard size="large" transparent={false} hover={false} className="modal-gaming" style={{ background: "rgba(10,14,24,0.98)", backdropFilter: "none", WebkitBackdropFilter: "none", color: "#e2e8f0" }}>
              <div className="modal-header">
                <h3>👥 Enter as a Pair</h3>
                <button type="button" className="close-button" onClick={() => setShowPartnerModal(false)} aria-label="Close">×</button>
              </div>
              <div className="modal-body">
                <p style={{ color: "#94a3b8", marginBottom: "1rem", fontSize: "0.9rem" }}>
                  This is a {tournament.play_format === "mixed" ? "mixed doubles" : "doubles"} tournament. Choose your partner.
                </p>
                <UserSearchInput
                  placeholder="Your doubles partner…"
                  selectedUser={partner}
                  onSelect={setPartner}
                  onClear={() => setPartner(null)}
                  excludeUserIds={user ? [user.id] : []}
                />
              </div>
              <div className="modal-actions">
                <FloatingButton variant="secondary" size="small" onClick={() => setShowPartnerModal(false)} disabled={registering}>Cancel</FloatingButton>
                <FloatingButton variant="success" size="small" disabled={!partner || registering} icon="✅"
                  onClick={() => submitRegistration({ partner_user_id: partner.id })}>
                  {registering ? "Registering…" : "Register Pair"}
                </FloatingButton>
              </div>
            </FloatingCard>
          </div>
        )}

        {/* ── Team Registration Modal ── */}
        {showTeamModal && (
          <div className="modal-overlay-gaming" role="dialog" aria-modal="true">
            <FloatingCard size="large" transparent={false} hover={false} className="modal-gaming" style={{ background: "rgba(10,14,24,0.98)", backdropFilter: "none", WebkitBackdropFilter: "none", color: "#e2e8f0" }}>
              <div className="modal-header">
                <h3>🏅 Register a Team</h3>
                <button type="button" className="close-button" onClick={() => setShowTeamModal(false)} aria-label="Close">×</button>
              </div>
              <div className="modal-body">
                <p style={{ color: "#94a3b8", marginBottom: "1rem", fontSize: "0.9rem" }}>
                  <strong style={{ color: "#a78bfa" }}>{tournament.sport_name}</strong> is a team sport.
                  Select one of your teams to register.
                </p>
                {loadingTeams ? (
                  <p style={{ color: "#94a3b8", textAlign: "center" }}>Loading your teams…</p>
                ) : userTeams.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "1rem" }}>
                    <p style={{ color: "#f87171", marginBottom: "0.5rem" }}>You are not a member of any active team.</p>
                    <p style={{ color: "#94a3b8", fontSize: "0.85rem" }}>Create or join a team first.</p>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                    {userTeams.map((team) => (
                      <label key={team.id} style={{ display: "flex", alignItems: "center", gap: "0.75rem", padding: "0.75rem 1rem",
                        background: selectedTeamId === String(team.id) ? "rgba(99,102,241,0.2)" : "rgba(255,255,255,0.04)",
                        border: `1px solid ${selectedTeamId === String(team.id) ? "#6366f1" : "rgba(255,255,255,0.1)"}`,
                        borderRadius: "0.5rem", cursor: "pointer", transition: "all 0.15s" }}>
                        <input type="radio" name="teamSelect" value={String(team.id)} checked={selectedTeamId === String(team.id)}
                          onChange={(e) => setSelectedTeamId(e.target.value)} style={{ accentColor: "#6366f1" }} />
                        <div>
                          <div style={{ color: "#e2e8f0", fontWeight: 600 }}>{team.name}</div>
                          {team.organization_name && <div style={{ color: "#94a3b8", fontSize: "0.8rem" }}>{team.organization_name}</div>}
                          <div style={{ color: "#a78bfa", fontSize: "0.8rem", textTransform: "capitalize" }}>
                            Your role: {team.member_role || "member"}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div className="modal-actions">
                <FloatingButton variant="secondary" size="small" onClick={() => setShowTeamModal(false)} disabled={registering}>Cancel</FloatingButton>
                {userTeams.length > 0 && (
                  <FloatingButton variant="success" size="small" disabled={!selectedTeamId || registering} icon="✅"
                    onClick={() => submitRegistration({ team_id: parseInt(selectedTeamId) })}>
                    {registering ? "Registering…" : "Register Team"}
                  </FloatingButton>
                )}
              </div>
            </FloatingCard>
          </div>
        )}

      </div>
    </GameLayout>
  );
};

export default TournamentDetails;
